/**
 * supabaseService.js - Database Operations Layer
 * 
 * Handles all Supabase database operations for the CPLC app.
 * This replaces localStorage with cloud database storage.
 */

import { supabase } from '../lib/supabase';
import bcrypt from 'bcryptjs';

// Password hashing configuration
const SALT_ROUNDS = 10;  // Industry standard, good balance of security and speed

// ============================================================================
// USER OPERATIONS
// ============================================================================

export const userService = {
  /**
   * Get all users
   */
  async getAll() {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching users:', error);
      return [];
    }
    
    // Transform from DB format to app format
    return data.map(user => ({
      id: user.id,
      username: user.username,
      password: user.password_hash,
      name: user.name,
      role: user.role,
      email: user.email,
      darkMode: user.dark_mode,
      headerColor: user.header_color,
      availability: user.availability || {},
      canViewAllStudents: user.can_view_all_students || false,
      availabilityStart: user.availability_start || null,
      availabilityEnd: user.availability_end || null
    }));
  },

  /**
   * Get user by ID
   */
  async getById(id) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error('Error fetching user:', error);
      return null;
    }
    
    return {
      id: data.id,
      username: data.username,
      password: data.password_hash,
      name: data.name,
      role: data.role,
      email: data.email,
      darkMode: data.dark_mode,
      headerColor: data.header_color,
      availability: data.availability || {},
      canViewAllStudents: data.can_view_all_students || false,
      availabilityStart: data.availability_start || null,
      availabilityEnd: data.availability_end || null
    };
  },

  /**
   * Get user by username (for login)
   */
  async getByUsername(username) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('username', username)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching user by username:', error);
      return null;
    }
    
    return {
      id: data.id,
      username: data.username,
      password: data.password_hash,
      name: data.name,
      role: data.role,
      email: data.email,
      darkMode: data.dark_mode,
      headerColor: data.header_color,
      availability: data.availability || {},
      canViewAllStudents: data.can_view_all_students || false,
      availabilityStart: data.availability_start || null,
      availabilityEnd: data.availability_end || null
    };
  },

  /**
   * Create a new user
   */
  async create(user) {
    // Hash the password before storing
    const hashedPassword = await bcrypt.hash(user.password, SALT_ROUNDS);
    
    const { data, error } = await supabase
      .from('users')
      .insert({
        username: user.username,
        password_hash: hashedPassword,
        name: user.name,
        role: user.role || 'USER',
        email: user.email || null,
        dark_mode: user.darkMode || false,
        header_color: user.headerColor || null,
        availability: user.availability || null,
        can_view_all_students: user.canViewAllStudents || false
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating user:', error);
      throw new Error(error.message);
    }
    
    return {
      id: data.id,
      username: data.username,
      password: data.password_hash,  // This is now the hash
      name: data.name,
      role: data.role,
      email: data.email,
      darkMode: data.dark_mode,
      headerColor: data.header_color,
      availability: data.availability || {},
      canViewAllStudents: data.can_view_all_students || false
    };
  },

  /**
   * Update a user
   */
  async update(id, updates) {
    const dbUpdates = {};
    if (updates.username !== undefined) dbUpdates.username = updates.username;
    
    // Hash password if being updated
    if (updates.password !== undefined) {
      dbUpdates.password_hash = await bcrypt.hash(updates.password, SALT_ROUNDS);
    }
    
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.role !== undefined) dbUpdates.role = updates.role;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.darkMode !== undefined) dbUpdates.dark_mode = updates.darkMode;
    if (updates.headerColor !== undefined) dbUpdates.header_color = updates.headerColor;
    if (updates.availability !== undefined) dbUpdates.availability = updates.availability;
    if (updates.canViewAllStudents !== undefined) dbUpdates.can_view_all_students = updates.canViewAllStudents;
    if (updates.availabilityStart !== undefined) dbUpdates.availability_start = updates.availabilityStart;
    if (updates.availabilityEnd !== undefined) dbUpdates.availability_end = updates.availabilityEnd;
    dbUpdates.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('users')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating user:', error);
      throw new Error(error.message);
    }
    
    return {
      id: data.id,
      username: data.username,
      password: data.password_hash,
      name: data.name,
      role: data.role,
      email: data.email,
      darkMode: data.dark_mode,
      headerColor: data.header_color,
      availability: data.availability || {},
      canViewAllStudents: data.can_view_all_students || false,
      availabilityStart: data.availability_start || null,
      availabilityEnd: data.availability_end || null
    };
  },

  /**
   * Delete a user
   */
  async delete(id) {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting user:', error);
      throw new Error(error.message);
    }
    return true;
  },

  /**
   * Verify a password against a hash
   * @param {string} plainPassword - The password to verify
   * @param {string} hashedPassword - The stored hash to compare against
   * @returns {Promise<boolean>} - True if password matches
   */
  async verifyPassword(plainPassword, hashedPassword) {
    return bcrypt.compare(plainPassword, hashedPassword);
  },

  /**
   * Hash a password (utility for migrations)
   * @param {string} plainPassword - The password to hash
   * @returns {Promise<string>} - The hashed password
   */
  async hashPassword(plainPassword) {
    return bcrypt.hash(plainPassword, SALT_ROUNDS);
  }
};


// ============================================================================
// ROOM OPERATIONS
// ============================================================================

export const roomService = {
  async getAll() {
    const { data, error } = await supabase
      .from('rooms')
      .select('*')
      .order('display_order');
    
    if (error) {
      console.error('Error fetching rooms:', error);
      return [];
    }
    
    return data.map(room => ({
      id: room.id,
      name: room.name,
      shortName: room.short_name,
      ratio: room.ratio,
      ageRange: room.age_range,
      displayOrder: room.display_order,
      isVisible: room.is_visible,
      showOnStudentSchedule: room.show_on_student_schedule !== false,
      showOnTeacherSchedule: room.show_on_teacher_schedule !== false,
      isUtility: room.is_utility || false
    }));
  },

  async create(room) {
    const { data, error } = await supabase
      .from('rooms')
      .insert({
        name: room.name,
        short_name: room.shortName || null,
        ratio: room.ratio || null,
        age_range: room.ageRange || null,
        display_order: room.displayOrder || 0,
        is_visible: room.isVisible !== false,
        show_on_student_schedule: room.showOnStudentSchedule !== false,
        show_on_teacher_schedule: room.showOnTeacherSchedule !== false,
        is_utility: room.isUtility || false
      })
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    
    return {
      id: data.id,
      name: data.name,
      shortName: data.short_name,
      ratio: data.ratio,
      ageRange: data.age_range,
      displayOrder: data.display_order,
      isVisible: data.is_visible,
      showOnStudentSchedule: data.show_on_student_schedule !== false,
      showOnTeacherSchedule: data.show_on_teacher_schedule !== false,
      isUtility: data.is_utility || false
    };
  },

  async update(id, updates) {
    const dbUpdates = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.shortName !== undefined) dbUpdates.short_name = updates.shortName;
    if (updates.ratio !== undefined) dbUpdates.ratio = updates.ratio;
    if (updates.ageRange !== undefined) dbUpdates.age_range = updates.ageRange;
    if (updates.displayOrder !== undefined) dbUpdates.display_order = updates.displayOrder;
    if (updates.isVisible !== undefined) dbUpdates.is_visible = updates.isVisible;
    if (updates.showOnStudentSchedule !== undefined) dbUpdates.show_on_student_schedule = updates.showOnStudentSchedule;
    if (updates.showOnTeacherSchedule !== undefined) dbUpdates.show_on_teacher_schedule = updates.showOnTeacherSchedule;
    if (updates.isUtility !== undefined) dbUpdates.is_utility = updates.isUtility;

    const { data, error } = await supabase
      .from('rooms')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    
    return {
      id: data.id,
      name: data.name,
      shortName: data.short_name,
      ratio: data.ratio,
      ageRange: data.age_range,
      displayOrder: data.display_order,
      isVisible: data.is_visible,
      showOnStudentSchedule: data.show_on_student_schedule !== false,
      showOnTeacherSchedule: data.show_on_teacher_schedule !== false,
      isUtility: data.is_utility || false
    };
  },

  async delete(id) {
    const { error } = await supabase.from('rooms').delete().eq('id', id);
    if (error) throw new Error(error.message);
    return true;
  }
};

// ============================================================================
// SCHEDULE OPERATIONS
// ============================================================================

export const scheduleService = {
  async getAll() {
    // Supabase has a server-side max limit of 1000 rows per request
    // Use pagination to fetch all records
    const PAGE_SIZE = 1000;
    let allData = [];
    let page = 0;
    let hasMore = true;
    
    while (hasMore) {
      const { data, error } = await supabase
        .from('schedules')
        .select('*')
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)
        .order('date', { ascending: true });
      
      if (error) {
        console.error('Error fetching schedules page', page, ':', error);
        break;
      }
      
      if (data && data.length > 0) {
        allData = allData.concat(data);
        hasMore = data.length === PAGE_SIZE;
        page++;
      } else {
        hasMore = false;
      }
    }
    
    // Transform to app format: { 'YYYY-MM-DD': { 'room-id': [assignments] } }
    const schedule = {};
    allData.forEach(row => {
      if (!schedule[row.date]) schedule[row.date] = {};
      if (!schedule[row.date][row.room_id]) schedule[row.date][row.room_id] = [];
      schedule[row.date][row.room_id].push({
        id: row.id,
        odId: row.id,
        userId: row.user_id,
        odUserId: row.user_id,
        startTime: row.start_time,
        endTime: row.end_time
      });
    });
    
    return schedule;
  },

  async getByDateRange(startDate, endDate) {
    const { data, error } = await supabase
      .from('schedules')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate);
    
    if (error) {
      console.error('Error fetching schedules:', error);
      return {};
    }
    
    const schedule = {};
    data.forEach(row => {
      if (!schedule[row.date]) schedule[row.date] = {};
      if (!schedule[row.date][row.room_id]) schedule[row.date][row.room_id] = [];
      schedule[row.date][row.room_id].push({
        odId: row.id,
        userId: row.user_id,
        startTime: row.start_time,
        endTime: row.end_time
      });
    });
    
    return schedule;
  },

  async addAssignment(date, roomId, userId, startTime, endTime) {
    const { data, error } = await supabase
      .from('schedules')
      .insert({
        date: date,
        room_id: roomId,
        user_id: userId,
        start_time: startTime,
        end_time: endTime
      })
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  async removeAssignment(id) {
    const { error } = await supabase.from('schedules').delete().eq('id', id);
    if (error) throw new Error(error.message);
    return true;
  },

  async clearDay(date) {
    const { error } = await supabase.from('schedules').delete().eq('date', date);
    if (error) throw new Error(error.message);
    return true;
  }
};


// ============================================================================
// PTO REQUEST OPERATIONS
// ============================================================================

export const ptoService = {
  async getAll() {
    const { data, error } = await supabase
      .from('pto_requests')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching PTO requests:', error);
      return [];
    }
    
    return data.map(pto => ({
      id: pto.id,
      userId: pto.user_id,
      dates: pto.dates,
      reason: pto.reason,
      status: pto.status,
      isPartialDay: pto.is_partial_day,
      partialStartTime: pto.partial_start_time,
      partialHours: pto.partial_hours,
      reviewedBy: pto.reviewed_by,
      reviewedAt: pto.reviewed_at,
      createdAt: pto.created_at
    }));
  },

  async getByUserId(userId) {
    const { data, error } = await supabase
      .from('pto_requests')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) return [];
    
    return data.map(pto => ({
      id: pto.id,
      userId: pto.user_id,
      dates: pto.dates,
      reason: pto.reason,
      status: pto.status,
      isPartialDay: pto.is_partial_day,
      partialStartTime: pto.partial_start_time,
      partialHours: pto.partial_hours,
      reviewedBy: pto.reviewed_by,
      reviewedAt: pto.reviewed_at,
      createdAt: pto.created_at
    }));
  },

  async create(request) {
    const { data, error } = await supabase
      .from('pto_requests')
      .insert({
        user_id: request.userId,
        dates: request.dates,
        reason: request.reason || null,
        status: 'Pending',
        is_partial_day: request.isPartialDay || false,
        partial_start_time: request.partialStartTime || null,
        partial_hours: request.partialHours || null
      })
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    
    return {
      id: data.id,
      userId: data.user_id,
      dates: data.dates,
      reason: data.reason,
      status: data.status,
      isPartialDay: data.is_partial_day,
      partialStartTime: data.partial_start_time,
      partialHours: data.partial_hours,
      createdAt: data.created_at
    };
  },

  async updateStatus(id, status, reviewedBy) {
    const { data, error } = await supabase
      .from('pto_requests')
      .update({
        status: status,
        reviewed_by: reviewedBy,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  async delete(id) {
    const { error } = await supabase.from('pto_requests').delete().eq('id', id);
    if (error) throw new Error(error.message);
    return true;
  }
};

// ============================================================================
// CHILDREN OPERATIONS
// ============================================================================

export const childService = {
  async getAll() {
    const { data, error } = await supabase
      .from('children')
      .select('*')
      .order('last_name');
    
    if (error) {
      console.error('Error fetching children:', error);
      return [];
    }
    
    return data.map(child => ({
      id: child.id,
      firstName: child.first_name,
      lastName: child.last_name,
      dateOfBirth: child.date_of_birth,
      primaryRoomId: child.primary_room_id,
      status: child.status,
      notes: child.notes,
      scheduledDays: child.scheduled_days || ['M','T','W','TH','F'],
      enrollmentDate: child.enrollment_date,
      parentName: child.parent_name,
      parentPhone: child.parent_phone,
      parentEmail: child.parent_email,
      useCustomHours: child.use_custom_hours || false,
      customHoursStart: child.custom_hours_start || '08:00',
      customHoursEnd: child.custom_hours_end || '17:00'
    }));
  },

  async create(child) {
    const { data, error } = await supabase
      .from('children')
      .insert({
        first_name: child.firstName,
        last_name: child.lastName,
        date_of_birth: child.dateOfBirth || null,
        primary_room_id: child.primaryRoomId || null,
        status: child.status || 'active',
        notes: child.notes || null,
        scheduled_days: child.scheduledDays || ['M','T','W','TH','F'],
        enrollment_date: child.enrollmentDate || null,
        parent_name: child.parentName || null,
        parent_phone: child.parentPhone || null,
        parent_email: child.parentEmail || null,
        use_custom_hours: child.useCustomHours || false,
        custom_hours_start: child.customHoursStart || '08:00',
        custom_hours_end: child.customHoursEnd || '17:00'
      })
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    
    return {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      dateOfBirth: data.date_of_birth,
      primaryRoomId: data.primary_room_id,
      status: data.status,
      notes: data.notes,
      scheduledDays: data.scheduled_days || ['M','T','W','TH','F'],
      enrollmentDate: data.enrollment_date,
      parentName: data.parent_name,
      parentPhone: data.parent_phone,
      parentEmail: data.parent_email,
      useCustomHours: data.use_custom_hours || false,
      customHoursStart: data.custom_hours_start || '08:00',
      customHoursEnd: data.custom_hours_end || '17:00'
    };
  },

  async update(id, updates) {
    const dbUpdates = {};
    if (updates.firstName !== undefined) dbUpdates.first_name = updates.firstName;
    if (updates.lastName !== undefined) dbUpdates.last_name = updates.lastName;
    if (updates.dateOfBirth !== undefined) dbUpdates.date_of_birth = updates.dateOfBirth;
    if (updates.primaryRoomId !== undefined) dbUpdates.primary_room_id = updates.primaryRoomId;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
    if (updates.scheduledDays !== undefined) dbUpdates.scheduled_days = updates.scheduledDays;
    if (updates.enrollmentDate !== undefined) dbUpdates.enrollment_date = updates.enrollmentDate;
    if (updates.parentName !== undefined) dbUpdates.parent_name = updates.parentName;
    if (updates.parentPhone !== undefined) dbUpdates.parent_phone = updates.parentPhone;
    if (updates.parentEmail !== undefined) dbUpdates.parent_email = updates.parentEmail;
    if (updates.useCustomHours !== undefined) dbUpdates.use_custom_hours = updates.useCustomHours;
    if (updates.customHoursStart !== undefined) dbUpdates.custom_hours_start = updates.customHoursStart;
    if (updates.customHoursEnd !== undefined) dbUpdates.custom_hours_end = updates.customHoursEnd;

    const { data, error } = await supabase
      .from('children')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    
    return {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      dateOfBirth: data.date_of_birth,
      primaryRoomId: data.primary_room_id,
      status: data.status,
      notes: data.notes,
      scheduledDays: data.scheduled_days || ['M','T','W','TH','F'],
      enrollmentDate: data.enrollment_date,
      parentName: data.parent_name,
      parentPhone: data.parent_phone,
      parentEmail: data.parent_email,
      useCustomHours: data.use_custom_hours || false,
      customHoursStart: data.custom_hours_start || '08:00',
      customHoursEnd: data.custom_hours_end || '17:00'
    };
  },

  async delete(id) {
    const { error } = await supabase.from('children').delete().eq('id', id);
    if (error) throw new Error(error.message);
    return true;
  }
};


// ============================================================================
// CHILD SCHEDULE OPERATIONS
// ============================================================================

export const childScheduleService = {
  async getAll() {
    console.log('childScheduleService.getAll: Fetching all child schedules...');
    
    // Supabase has a server-side max limit of 1000 rows per request
    // Use pagination to fetch all records
    const PAGE_SIZE = 1000;
    let allData = [];
    let page = 0;
    let hasMore = true;
    
    while (hasMore) {
      const { data, error } = await supabase
        .from('child_schedules')
        .select('*')
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)
        .order('date', { ascending: true });
      
      if (error) {
        console.error('Error fetching child schedules page', page, ':', error);
        break;
      }
      
      if (data && data.length > 0) {
        allData = allData.concat(data);
        console.log(`childScheduleService.getAll: Page ${page + 1} fetched ${data.length} rows (total: ${allData.length})`);
        hasMore = data.length === PAGE_SIZE;
        page++;
      } else {
        hasMore = false;
      }
    }
    
    console.log('childScheduleService.getAll: Total rows fetched:', allData.length);
    
    // Transform to app format: { 'YYYY-MM-DD': { 'child-id': { roomId, ... } } }
    const schedule = {};
    allData.forEach(row => {
      if (!schedule[row.date]) schedule[row.date] = {};
      schedule[row.date][row.child_id] = {
        roomId: row.room_id,
        startTime: row.start_time || null,
        endTime: row.end_time || null,
        isVisit: row.is_visit || false,
        visitNote: row.visit_note || null,
        isAbsent: row.is_absent || false
      };
    });
    
    // Log summary by date
    const datesSummary = {};
    Object.keys(schedule).forEach(date => {
      datesSummary[date] = Object.keys(schedule[date]).length;
    });
    console.log('childScheduleService.getAll: Entries by date:', datesSummary);
    
    return schedule;
  },

  async getByDateRange(startDate, endDate) {
    const { data, error } = await supabase
      .from('child_schedules')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate);
    
    if (error) return {};
    
    const schedule = {};
    data.forEach(row => {
      if (!schedule[row.date]) schedule[row.date] = {};
      schedule[row.date][row.child_id] = {
        roomId: row.room_id,
        startTime: row.start_time || null,
        endTime: row.end_time || null,
        isVisit: row.is_visit || false,
        visitNote: row.visit_note || null,
        isAbsent: row.is_absent || false
      };
    });
    
    return schedule;
  },

  async addChild(date, roomId, childId, options = {}) {
    const { data, error } = await supabase
      .from('child_schedules')
      .upsert(
        { 
          date, 
          room_id: roomId, 
          child_id: childId,
          is_visit: options.isVisit || false,
          visit_note: options.visitNote || null,
          start_time: options.startTime || null,
          end_time: options.endTime || null,
          is_absent: options.isAbsent || false
        },
        { onConflict: 'child_id,date' }
      )
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  async updateChildAbsent(date, childId, isAbsent) {
    const { data, error } = await supabase
      .from('child_schedules')
      .update({ is_absent: isAbsent })
      .eq('date', date)
      .eq('child_id', childId)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  async removeChild(date, childId) {
    const { error } = await supabase
      .from('child_schedules')
      .delete()
      .eq('date', date)
      .eq('child_id', childId);
    
    if (error) throw new Error(error.message);
    return true;
  },

  async moveChild(date, childId, newRoomId) {
    const { data, error } = await supabase
      .from('child_schedules')
      .update({ room_id: newRoomId })
      .eq('date', date)
      .eq('child_id', childId)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  },

  /**
   * Bulk add multiple children to schedule in a single database call
   * Batches inserts in chunks to avoid Supabase limits
   * @param {Array} entries - Array of { date, roomId, childId, isVisit, visitNote, startTime, endTime, isAbsent }
   * @returns {Object} - { inserted: number, errors: Array }
   */
  async addChildrenBulk(entries) {
    if (!entries || entries.length === 0) return { inserted: 0, errors: [] };
    
    // Transform to database format
    const rows = entries.map(entry => ({
      date: entry.date,
      room_id: entry.roomId,
      child_id: entry.childId,
      is_visit: entry.isVisit || false,
      visit_note: entry.visitNote || null,
      start_time: entry.startTime || null,
      end_time: entry.endTime || null,
      is_absent: entry.isAbsent || false
    }));
    
    console.log(`Bulk inserting ${rows.length} child schedule entries...`);
    
    // Batch in chunks of 50 to avoid Supabase limits
    const BATCH_SIZE = 50;
    let totalInserted = 0;
    const errors = [];
    
    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
      const batch = rows.slice(i, i + BATCH_SIZE);
      console.log(`  Inserting batch ${Math.floor(i/BATCH_SIZE) + 1}: ${batch.length} rows (dates: ${batch[0]?.date} to ${batch[batch.length-1]?.date})`);
      
      const { data, error } = await supabase
        .from('child_schedules')
        .upsert(batch, { onConflict: 'child_id,date' })
        .select();
      
      if (error) {
        console.error(`Batch ${Math.floor(i/BATCH_SIZE) + 1} error:`, error);
        errors.push(error.message);
      } else {
        totalInserted += data?.length || 0;
        console.log(`  Batch ${Math.floor(i/BATCH_SIZE) + 1} success: ${data?.length || 0} rows inserted`);
      }
    }
    
    console.log(`Bulk insert complete: ${totalInserted}/${rows.length} rows inserted`);
    
    return { inserted: totalInserted, errors };
  },

  /**
   * Delete all child schedule entries for a date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Object} - { deleted: number }
   */
  async deleteByDateRange(startDate, endDate) {
    const { data, error } = await supabase
      .from('child_schedules')
      .delete()
      .gte('date', startDate)
      .lte('date', endDate)
      .select();
    
    if (error) throw new Error(error.message);
    return { deleted: data?.length || 0 };
  },

  /**
   * Delete all child schedule entries for a single date
   * @param {string} date - Date (YYYY-MM-DD)
   * @returns {Object} - { deleted: number }
   */
  async deleteByDate(date) {
    const { data, error } = await supabase
      .from('child_schedules')
      .delete()
      .eq('date', date)
      .select();
    
    if (error) throw new Error(error.message);
    return { deleted: data?.length || 0 };
  },

  /**
   * Delete child schedule entries older than specified days (default 90)
   * Returns count of deleted entries
   */
  async cleanup(retentionDays = 90) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays);
    const cutoffStr = cutoffDate.toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('child_schedules')
      .delete()
      .lt('date', cutoffStr)
      .select('id');

    if (error) {
      console.error('Error cleaning up child schedules:', error);
      return 0;
    }

    const count = data?.length || 0;
    if (count > 0) {
      console.log(`Child schedule cleanup: removed ${count} entries older than ${retentionDays} days`);
    }
    return count;
  }
};

// ============================================================================
// NOTES OPERATIONS
// ============================================================================

export const notesService = {
  // Daily notes (week themes)
  async getDailyNotes() {
    const { data, error } = await supabase
      .from('daily_notes')
      .select('*');
    
    if (error) return {};
    
    const notes = {};
    data.forEach(row => {
      notes[row.date] = { note: row.note, type: row.type || 'daily' };
    });
    return notes;
  },

  async setDailyNote(date, note, type = 'daily') {
    const { error } = await supabase
      .from('daily_notes')
      .upsert({ date, note, type }, { onConflict: 'date' });
    
    if (error) throw new Error(error.message);
    return true;
  },

  // Room day notes
  async getRoomDayNotes() {
    const { data, error } = await supabase
      .from('room_day_notes')
      .select('*');
    
    if (error) return {};
    
    // Format: { 'YYYY-MM-DD': { 'room-id': 'note' } }
    const notes = {};
    data.forEach(row => {
      if (!notes[row.date]) notes[row.date] = {};
      notes[row.date][row.room_id] = row.note;
    });
    return notes;
  },

  async setRoomDayNote(date, roomId, note) {
    if (!note || note.trim() === '') {
      // Delete if empty
      await supabase
        .from('room_day_notes')
        .delete()
        .eq('date', date)
        .eq('room_id', roomId);
      return true;
    }

    const { error } = await supabase
      .from('room_day_notes')
      .upsert(
        { date, room_id: roomId, note },
        { onConflict: 'room_id,date' }
      );
    
    if (error) throw new Error(error.message);
    return true;
  }
};

// ============================================================================
// AUDIT LOG OPERATIONS
// ============================================================================

export const auditService = {
  async getAll(maxEntries = 5000) {
    // Supabase has a server-side max limit of 1000 rows per request
    // Use pagination to fetch all records up to maxEntries
    const PAGE_SIZE = 1000;
    let allData = [];
    let page = 0;
    let hasMore = true;
    
    while (hasMore && allData.length < maxEntries) {
      const { data, error } = await supabase
        .from('audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
      
      if (error) {
        console.error('Error fetching audit log page', page, ':', error);
        break;
      }
      
      if (data && data.length > 0) {
        allData = allData.concat(data);
        hasMore = data.length === PAGE_SIZE;
        page++;
      } else {
        hasMore = false;
      }
    }
    
    // Trim to maxEntries if we fetched more
    if (allData.length > maxEntries) {
      allData = allData.slice(0, maxEntries);
    }
    
    console.log(`Audit log: fetched ${allData.length} entries`);
    
    return allData.map(log => ({
      id: log.id,
      actorId: log.user_id,
      actorName: log.user_name || 'System',
      action: log.action,
      targetType: log.target_type,
      targetId: log.target_id,
      details: log.details || {},
      description: log.details?.description || `${log.user_name || 'System'} performed ${log.action?.replace(/_/g, ' ').toLowerCase() || 'action'}`,
      timestamp: log.created_at
    }));
  },

  async add(entry) {
    const { error } = await supabase
      .from('audit_log')
      .insert({
        user_id: entry.userId || null,
        user_name: entry.userName || null,
        action: entry.action,
        target_type: entry.targetType || null,
        target_id: entry.targetId || null,
        details: entry.details || null
      });
    
    if (error) console.error('Error adding audit log:', error);
    return !error;
  },

  /**
   * Delete audit log entries older than specified days (default 90)
   * Returns count of deleted entries
   */
  async cleanup(retentionDays = 90) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays);
    const cutoffISO = cutoffDate.toISOString();

    const { data, error } = await supabase
      .from('audit_log')
      .delete()
      .lt('created_at', cutoffISO)
      .select('id');

    if (error) {
      console.error('Error cleaning up audit log:', error);
      return 0;
    }

    const count = data?.length || 0;
    if (count > 0) {
      console.log(`Audit log cleanup: removed ${count} entries older than ${retentionDays} days`);
    }
    return count;
  },

  /**
   * Clear user references from audit log (set user_id to null)
   * Call this before deleting a user to avoid foreign key constraint violation
   */
  async clearUserReferences(userId) {
    const { error } = await supabase
      .from('audit_log')
      .update({ user_id: null })
      .eq('user_id', userId);

    if (error) {
      console.error('Error clearing user references from audit log:', error);
      return false;
    }
    return true;
  }
};

// ============================================================================
// SCHEDULE TEMPLATE OPERATIONS
// ============================================================================

export const templateService = {
  async getAll() {
    const { data, error } = await supabase
      .from('schedule_templates')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
    
    return data.map(t => ({
      id: t.id,
      name: t.name,
      description: t.description || '',
      assignments: t.assignments,
      createdBy: t.created_by,
      createdAt: t.created_at,
      updatedAt: t.updated_at || t.created_at
    }));
  },

  async create(template) {
    const { data, error } = await supabase
      .from('schedule_templates')
      .insert({
        name: template.name,
        description: template.description || '',
        assignments: template.assignments,
        created_by: template.createdBy || null
      })
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    
    return {
      id: data.id,
      name: data.name,
      description: data.description || '',
      assignments: data.assignments,
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at || data.created_at
    };
  },

  async update(id, updates) {
    const dbUpdates = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.assignments !== undefined) dbUpdates.assignments = updates.assignments;

    const { data, error } = await supabase
      .from('schedule_templates')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    
    return {
      id: data.id,
      name: data.name,
      description: data.description || '',
      assignments: data.assignments,
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at || data.created_at
    };
  },

  async delete(id) {
    const { error } = await supabase.from('schedule_templates').delete().eq('id', id);
    if (error) throw new Error(error.message);
    return true;
  }
};

// ============================================================================
// SCHEDULE PUBLISH STATUS OPERATIONS
// ============================================================================

export const schedulePublishService = {
  /**
   * Get all published week records
   */
  async getAll() {
    const { data, error } = await supabase
      .from('schedule_publish_status')
      .select('*');
    
    if (error) {
      console.error('Error fetching publish status:', error);
      return {};
    }
    
    // Transform to { 'YYYY-MM-DD': boolean }
    const published = {};
    data.forEach(row => {
      published[row.week_start] = row.is_published;
    });
    return published;
  },

  /**
   * Check if a specific week is published
   */
  async isWeekPublished(weekStart) {
    const { data, error } = await supabase
      .from('schedule_publish_status')
      .select('is_published')
      .eq('week_start', weekStart)
      .single();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error checking publish status:', error);
      return false;
    }
    
    return data?.is_published || false;
  },

  /**
   * Set publish status for a week
   */
  async setPublishStatus(weekStart, isPublished, publishedBy = null) {
    const { data, error } = await supabase
      .from('schedule_publish_status')
      .upsert({
        week_start: weekStart,
        is_published: isPublished,
        published_by: publishedBy,
        published_at: isPublished ? new Date().toISOString() : null
      }, { onConflict: 'week_start' })
      .select()
      .single();
    
    if (error) throw new Error(error.message);
    return data;
  }
};


// ============================================================================
// COMBINED EXPORT
// ============================================================================

export default {
  users: userService,
  rooms: roomService,
  schedules: scheduleService,
  pto: ptoService,
  children: childService,
  childSchedules: childScheduleService,
  notes: notesService,
  audit: auditService,
  templates: templateService,
  schedulePublish: schedulePublishService
};
